import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileDown, Printer } from "lucide-react";
import { getProgram, getCoachingAthlete } from "../../lib/api";
import { BrandBanner, BrandFooter } from "../../components/ReportFrame";
import { RichTextView, looksLikeHtml, decodeEntities } from "../../components/RichTextEditor";
import {
  workoutTypeLabel,
  workoutTypeColor,
  formatSegmentValue,
  strengthBlockLabel,
  weekDateRange,
  formatDateRange,
  formatStrengthIntensity,
  weekVolume,
  formatVolume,
  groupWeeksByPhase,
  ZONE_RPE_MAP,
} from "../../lib/coaching";
import { toast } from "sonner";
import { linkify, shortLinkLabel } from "../../lib/linkify";

/* ---------- Shared renderers (mirrors ProgramWeekReportPage) ---------- */

function SegmentLine({ seg, indent = false }) {
  if (!seg) return null;
  const meta = formatSegmentValue(seg);
  return (
    <div className={`flex gap-2 text-[10px] leading-relaxed ${indent ? "pl-4" : ""}`}>
      {seg.label && (
        <span className="text-[var(--tir-muted)] uppercase tracking-[0.08em] text-[9px] font-semibold shrink-0 min-w-[52px]">
          {seg.label}
        </span>
      )}
      <span className="text-[var(--tir-ink)] font-mono-tir">{meta || "—"}</span>
      {seg.notes && <span className="italic text-[var(--tir-muted)]">{linkify(seg.notes)}</span>}
    </div>
  );
}

function GroupBlock({ seg }) {
  return (
    <div className="border-l-2 border-[var(--tir-ink)] pl-2 py-0.5">
      <div className="text-[10px] font-semibold text-[var(--tir-ink)] tracking-[0.08em] uppercase">
        {seg.label}{" "}
        {seg.repeat_count ? <span className="text-[var(--tir-burgundy)]">· ×{seg.repeat_count}</span> : null}
      </div>
      <div className="space-y-0.5">
        {(seg.items || []).map((s) => (
          <SegmentLine key={s.id} seg={s} indent />
        ))}
      </div>
    </div>
  );
}

function StrengthBlockRender({ block }) {
  const header = [strengthBlockLabel(block.kind)];
  if (block.rounds) header.push(`${block.rounds} rounds`);
  if (block.time_cap_minutes) header.push(`${block.time_cap_minutes} min cap`);
  return (
    <div className="border-l-2 border-[var(--tir-burgundy)] pl-2 py-0.5 my-1">
      <div className="text-[10px] font-semibold text-[var(--tir-burgundy)] tracking-[0.08em] uppercase mb-0.5">
        {(block.label ? `${block.label} · ` : "") + header.join(" · ")}
      </div>
      {block.notes && (
        <div className="text-[9px] italic text-[var(--tir-muted)] mb-1">{linkify(block.notes)}</div>
      )}
      <table className="w-full text-[9px] border-collapse">
        <thead>
          <tr className="border-b border-[var(--tir-border)] text-[var(--tir-muted)] uppercase tracking-[0.08em]">
            <th className="text-left font-semibold py-0.5 pr-2">Exercise</th>
            <th className="text-center font-semibold py-0.5 px-1 w-10">Sets</th>
            <th className="text-center font-semibold py-0.5 px-1 w-12">Reps</th>
            <th className="text-center font-semibold py-0.5 px-1 w-14">Intensity</th>
            <th className="text-center font-semibold py-0.5 px-1 w-10">Rest</th>
            <th className="text-left font-semibold py-0.5 pl-2">Notes</th>
          </tr>
        </thead>
        <tbody>
          {(block.exercises || []).map((ex) => (
            <tr key={ex.id} className="border-b border-[var(--tir-border)]/60">
              <td className="py-0.5 pr-2 text-[var(--tir-ink)] font-medium">{ex.name || "—"}</td>
              <td className="text-center py-0.5 px-1 font-mono-tir">{ex.sets || ""}</td>
              <td className="text-center py-0.5 px-1 font-mono-tir">{ex.reps || ""}</td>
              <td className="text-center py-0.5 px-1 font-mono-tir">{formatStrengthIntensity(ex)}</td>
              <td className="text-center py-0.5 px-1 font-mono-tir">{ex.rest || ""}</td>
              <td className="py-0.5 pl-2 italic text-[var(--tir-muted)]">{ex.notes ? linkify(ex.notes) : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WorkoutBlock({ workout }) {
  if (!workout) return null;
  const label = workout.title?.trim() || workoutTypeLabel(workout.type);
  const color = workoutTypeColor(workout.type);
  const slot = (workout.slot || "any").toUpperCase();
  const segments = workout.segments || [];
  const strengthBlocks = workout.strength_blocks || [];
  const legacyStructure = !segments.length && !strengthBlocks.length && workout.structure;
  return (
    <div
      className="border border-[var(--tir-border)] bg-white pl-2.5 pr-2.5 py-2"
      style={{ borderLeft: `4px solid ${color}` }}
    >
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          {workout.slot && workout.slot !== "any" && (
            <span className="text-[8px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
              {slot}
            </span>
          )}
          <span className="text-[11px] font-semibold text-[var(--tir-ink)] uppercase tracking-[0.08em]">
            {label}
          </span>
        </div>
        {workout.duration_or_distance && (
          <span className="text-[10px] font-mono-tir text-[var(--tir-ink)] whitespace-nowrap">
            {workout.duration_or_distance}
          </span>
        )}
      </div>
      {strengthBlocks.length > 0 && (
        <div className="space-y-1.5">
          {strengthBlocks.map((b) => (
            <StrengthBlockRender key={b.id} block={b} />
          ))}
        </div>
      )}
      {segments.length > 0 && (
        <div className="space-y-0.5">
          {segments.map((seg) =>
            seg.kind === "group" ? <GroupBlock key={seg.id} seg={seg} /> : <SegmentLine key={seg.id} seg={seg} />
          )}
        </div>
      )}
      {legacyStructure && (
        <pre className="whitespace-pre-wrap font-mono-tir text-[10px] leading-snug text-[var(--tir-ink)] mt-1.5" style={{ fontFamily: "inherit" }}>
          {workout.structure}
        </pre>
      )}
      {workout.add_strides && (
        <div className="text-[10px] text-[var(--tir-burgundy)] mt-1">
          + {workout.strides_count || ""} {workout.strides_kind === "hill_surges" ? "hill surges" : "strides"} in the last half
        </div>
      )}
      {(workout.external_link || workout.audio_link) && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[10px]">
          {workout.external_link && (
            <a
              href={workout.external_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--tir-burgundy)] underline font-mono-tir break-all"
              title={workout.external_link}
            >
              ↗ {shortLinkLabel(workout.external_link)}
            </a>
          )}
          {workout.audio_link && (
            <a
              href={workout.audio_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--tir-burgundy)] underline font-mono-tir break-all"
              title={workout.audio_link}
            >
              ♪ {shortLinkLabel(workout.audio_link)}
            </a>
          )}
        </div>
      )}
      {workout.session_note && (
        looksLikeHtml(workout.session_note) ? (
          <RichTextView
            html={workout.session_note}
            className="text-[10px] italic text-[var(--tir-muted)] mt-1 leading-snug"
          />
        ) : (
          <div className="text-[10px] italic text-[var(--tir-muted)] mt-1 leading-snug">
            {linkify(decodeEntities(workout.session_note))}
          </div>
        )
      )}
    </div>
  );
}

/* ---------- Full-program pages ---------- */

function CoverPage({ program, athleteName }) {
  return (
    <div className="print-page" data-testid="full-report-cover">
      <BrandBanner athleteName={athleteName || ""} testDate="" headline={program.title} />
      <div className="px-10 pt-12 pb-14">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-4">
          Training program
        </div>
        <h1 className="font-serif-display text-6xl leading-[1.03] text-[var(--tir-ink)] mb-6">
          {program.title}
        </h1>
        {program.goal && (
          <p className="text-lg text-[var(--tir-ink)] leading-relaxed max-w-xl mb-8">
            {program.goal}
          </p>
        )}

        <div className="grid grid-cols-2 gap-x-10 gap-y-6 border-t border-[var(--tir-border)] pt-8 max-w-xl">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">Duration</div>
            <div className="font-mono-tir text-lg mt-1">{program.total_weeks} weeks</div>
          </div>
          {program.experience_level && (
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">Level</div>
              <div className="font-mono-tir text-lg mt-1 capitalize">{program.experience_level}</div>
            </div>
          )}
          {program.max_mileage && (
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">Peak volume</div>
              <div className="font-mono-tir text-lg mt-1">{program.max_mileage}</div>
            </div>
          )}
          {program.prerequisite && (
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">Prerequisite</div>
              <div className="font-mono-tir text-sm mt-1">{program.prerequisite}</div>
            </div>
          )}
          {program.target_race && (
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">Target race</div>
              <div className="font-mono-tir text-sm mt-1">{program.target_race}</div>
            </div>
          )}
          {program.start_date && (
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">Starts</div>
              <div className="font-mono-tir text-sm mt-1">{program.start_date}</div>
            </div>
          )}
        </div>
      </div>
      <BrandFooter />
    </div>
  );
}

function HowToPage({ program, athleteName }) {
  return (
    <div className="print-page" data-testid="full-report-howto">
      <BrandBanner athleteName={athleteName || ""} testDate="" headline={program.title} />
      <div className="px-10 pt-10 pb-14">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-2">
          How to use this program
        </div>
        <h2 className="font-serif-display text-4xl text-[var(--tir-ink)] mb-6">
          Read this first.
        </h2>
        {program.intro_howto ? (
          looksLikeHtml(program.intro_howto) ? (
            <RichTextView
              html={program.intro_howto}
              className="text-sm text-[var(--tir-ink)] leading-relaxed max-w-3xl"
            />
          ) : (
            <div className="text-sm text-[var(--tir-ink)] leading-relaxed whitespace-pre-wrap max-w-3xl">
              {linkify(program.intro_howto)}
            </div>
          )
        ) : null}
      </div>
      <BrandFooter />
    </div>
  );
}

function ZonesPage({ program, athleteName }) {
  const zoneRows = [
    { z: 1, name: "Easy / Recovery", desc: "Conversational, fully aerobic." },
    { z: 2, name: "Aerobic / Steady", desc: "Comfortable; short phrases only." },
    { z: 3, name: "Tempo / Marathon", desc: "Moderately hard; focused breathing." },
    { z: 4, name: "Threshold", desc: "Hard but sustainable for ~30–45 min." },
    { z: 5, name: "VO₂ / Intervals", desc: "Very hard; short reps, full recovery." },
  ];
  return (
    <div className="print-page" data-testid="full-report-zones">
      <BrandBanner athleteName={athleteName || ""} testDate="" headline={program.title} />
      <div className="px-10 pt-10 pb-14">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-2">
          Zones & RPE
        </div>
        <h2 className="font-serif-display text-4xl text-[var(--tir-ink)] mb-6">
          Know your effort.
        </h2>
        <p className="text-sm text-[var(--tir-ink)] leading-relaxed max-w-3xl mb-6">
          Every session is prescribed by <b>zone</b> (physiological intensity) and{" "}
          <b>RPE</b> (rate of perceived exertion, 1–10). Lactate tests fine-tune these;
          in the meantime, use the feel descriptions below.
        </p>
        <table className="w-full text-sm border-collapse max-w-3xl">
          <thead>
            <tr className="border-b-2 border-[var(--tir-ink)] text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
              <th className="text-left py-2 pr-4 font-semibold w-20">Zone</th>
              <th className="text-left py-2 pr-4 font-semibold">Name</th>
              <th className="text-left py-2 pr-4 font-semibold">Feel</th>
              <th className="text-center py-2 font-semibold w-24">RPE</th>
            </tr>
          </thead>
          <tbody>
            {zoneRows.map((r) => {
              const rpe = ZONE_RPE_MAP[r.z];
              return (
                <tr key={r.z} className="border-b border-[var(--tir-border)]">
                  <td className="py-2.5 pr-4 font-mono-tir text-[var(--tir-burgundy)] font-semibold">
                    Z{r.z}
                  </td>
                  <td className="py-2.5 pr-4 text-[var(--tir-ink)] font-medium">{r.name}</td>
                  <td className="py-2.5 pr-4 text-[var(--tir-muted)] text-[13px]">{r.desc}</td>
                  <td className="py-2.5 text-center font-mono-tir text-[var(--tir-ink)]">
                    {rpe ? `${rpe[0]}–${rpe[1]}` : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <BrandFooter />
    </div>
  );
}

function ModifyPage({ program, athleteName }) {
  return (
    <div className="print-page" data-testid="full-report-modify">
      <BrandBanner athleteName={athleteName || ""} testDate="" headline={program.title} />
      <div className="px-10 pt-10 pb-14">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-2">
          How to modify
        </div>
        <h2 className="font-serif-display text-4xl text-[var(--tir-ink)] mb-6">
          When life happens.
        </h2>
        {program.intro_modify ? (
          looksLikeHtml(program.intro_modify) ? (
            <RichTextView
              html={program.intro_modify}
              className="text-sm text-[var(--tir-ink)] leading-relaxed max-w-3xl"
            />
          ) : (
            <div className="text-sm text-[var(--tir-ink)] leading-relaxed whitespace-pre-wrap max-w-3xl">
              {linkify(program.intro_modify)}
            </div>
          )
        ) : null}
      </div>
      <BrandFooter />
    </div>
  );
}

function WeekPage({ program, week, athleteName }) {
  const phase = week.phase_label;
  const weekLabel = phase ? `${phase} · Week ${week.week_number}` : `Week ${week.week_number}`;
  const [ws, we] = weekDateRange(program.start_date, week.week_number);
  const volume = formatVolume(weekVolume(week));
  return (
    <div
      className="print-page"
      data-testid={`full-report-week-${week.week_number}`}
      style={
        week.is_recovery
          ? { boxShadow: "inset 6px 0 0 #b7d4e0, 0 10px 40px -20px rgba(28, 25, 23, 0.25)" }
          : undefined
      }
    >
      <BrandBanner athleteName={athleteName || ""} testDate="" headline={program.title} />
      <div className="px-10 pt-7 pb-14">
        <div className="grid grid-cols-4 gap-6 border-b border-[var(--tir-border)] pb-4 mb-5">
          <div>
            <div className={`text-[10px] tracking-[0.25em] uppercase font-semibold ${week.is_recovery ? "text-[var(--tir-navy)]" : "text-[var(--tir-muted)]"}`}>
              {week.is_recovery ? "Recovery week" : "Training week"}
            </div>
            <div className="font-mono-tir text-sm mt-1">{weekLabel}</div>
            {ws && we && (
              <div className="font-mono-tir text-[11px] text-[var(--tir-muted)] mt-0.5">
                {formatDateRange(ws, we)}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">Weekly volume</div>
            <div className="font-mono-tir text-sm mt-1 text-[var(--tir-burgundy)]">{volume || "—"}</div>
          </div>
          <div className="col-span-2">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">Program goal</div>
            <div className="font-mono-tir text-sm mt-1">
              {program.goal || program.target_race || program.title}
            </div>
          </div>
        </div>

        {week.coach_note && (
          <div className="mb-6 border-l-2 border-[var(--tir-burgundy)] pl-4 py-1">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-burgundy)] font-semibold mb-1">
              Coach's note
            </div>
            {looksLikeHtml(week.coach_note) ? (
              <RichTextView
                html={week.coach_note}
                className="text-sm text-[var(--tir-ink)] leading-relaxed"
              />
            ) : (
              <p className="text-sm text-[var(--tir-ink)] leading-relaxed whitespace-pre-wrap">
                {linkify(week.coach_note)}
              </p>
            )}
          </div>
        )}

        <div className="space-y-3">
          {week.days.map((day) => (
            <div
              key={day.id}
              className="grid grid-cols-12 gap-3 border-b border-[var(--tir-border)] pb-3 last:border-b-0"
            >
              <div className="col-span-2 pt-1">
                <div className="font-serif-display text-xl text-[var(--tir-ink)] leading-tight">
                  {day.label}
                </div>
              </div>
              <div className="col-span-10 space-y-2">
                {(day.workouts || []).length === 0 ? (
                  <div className="text-[11px] italic text-[var(--tir-muted)] py-1.5">
                    Rest day or flexible
                  </div>
                ) : (
                  day.workouts.map((w) => <WorkoutBlock key={w.id} workout={w} />)
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <BrandFooter />
    </div>
  );
}

/* ---------- Page component ---------- */

function PhaseOverviewPage({ program, phaseGroups, athleteName }) {
  return (
    <div className="print-page" data-testid="full-report-overview">
      <BrandBanner athleteName={athleteName || ""} testDate="" headline={program.title} />
      <div className="px-10 pt-10 pb-14">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-2">
          Program overview
        </div>
        <h2 className="font-serif-display text-4xl text-[var(--tir-ink)] mb-6">
          Your arc, week by week.
        </h2>
        <div className="space-y-6">
          {phaseGroups.map((grp, i) => (
            <div key={i} className="border-l-4 border-[var(--tir-burgundy)] pl-5">
              <div className="flex items-baseline justify-between mb-2 flex-wrap gap-x-4">
                <div className="font-serif-display text-2xl text-[var(--tir-ink)]">
                  {grp.phase_label || "Unlabeled phase"}
                </div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                  W{grp.weeks[0].week.week_number}–W
                  {grp.weeks[grp.weeks.length - 1].week.week_number} ·{" "}
                  {grp.weeks.length} week{grp.weeks.length === 1 ? "" : "s"}
                </div>
              </div>
              <table className="w-full text-[11px] border-collapse">
                <thead>
                  <tr className="border-b border-[var(--tir-border)] text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                    <th className="text-left font-semibold py-1.5 pr-3 w-12">Wk</th>
                    <th className="text-left font-semibold py-1.5 pr-3">Theme / note</th>
                    <th className="text-right font-semibold py-1.5 pl-3 w-32">Volume</th>
                    <th className="text-center font-semibold py-1.5 pl-3 w-20">Focus</th>
                  </tr>
                </thead>
                <tbody>
                  {grp.weeks.map(({ week: w }) => {
                    const vol = formatVolume(weekVolume(w));
                    const note = (w.coach_note || "").split("\n")[0].slice(0, 90);
                    return (
                      <tr key={w.id} className={`border-b border-[var(--tir-border)]/60 ${w.is_recovery ? "bg-[#e8eef2]/40" : ""}`}>
                        <td className="py-1.5 pr-3 font-mono-tir text-[var(--tir-ink)] font-semibold">
                          W{w.week_number}
                        </td>
                        <td className="py-1.5 pr-3 text-[var(--tir-muted)]">{note || "—"}</td>
                        <td className="py-1.5 pl-3 text-right font-mono-tir text-[var(--tir-burgundy)]">
                          {vol || "—"}
                        </td>
                        <td className="py-1.5 pl-3 text-center text-[10px] tracking-[0.18em] uppercase">
                          {w.is_recovery ? (
                            <span className="text-[var(--tir-navy)] font-semibold">Recovery</span>
                          ) : (
                            <span className="text-[var(--tir-muted)]">Build</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
      <BrandFooter />
    </div>
  );
}

export default function ProgramFullReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const p = await getProgram(id);
        setProgram(p);
        if (p.athlete_id) {
          try {
            const a = await getCoachingAthlete(p.athlete_id);
            setAthlete(a);
          } catch {
            /* ignore */
          }
        }
      } catch {
        toast.error("Couldn't load program");
        navigate("/coaching");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const athleteName = athlete?.name || "";

  const pages = useMemo(() => {
    if (!program) return [];
    const list = [{ key: "cover", node: <CoverPage program={program} athleteName={athleteName} /> }];
    if ((program.intro_howto || "").trim()) {
      list.push({ key: "howto", node: <HowToPage program={program} athleteName={athleteName} /> });
    }
    list.push({ key: "zones", node: <ZonesPage program={program} athleteName={athleteName} /> });
    if ((program.intro_modify || "").trim()) {
      list.push({ key: "modify", node: <ModifyPage program={program} athleteName={athleteName} /> });
    }
    const phaseGroups = groupWeeksByPhase(program.weeks || []);
    // Only show the overview if there's meaningful structure (any phase label OR a recovery week)
    const hasStructure =
      phaseGroups.some((g) => g.phase_label) ||
      (program.weeks || []).some((w) => w.is_recovery);
    if (hasStructure) {
      list.push({
        key: "overview",
        node: (
          <PhaseOverviewPage program={program} phaseGroups={phaseGroups} athleteName={athleteName} />
        ),
      });
    }
    for (const w of program.weeks || []) {
      list.push({
        key: `week-${w.week_number}`,
        node: <WeekPage program={program} week={w} athleteName={athleteName} />,
      });
    }
    return list;
  }, [program, athleteName]);

  const exportPDF = async () => {
    if (!rootRef.current) return;
    setExporting(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const pageWidth = 210;
      const pageHeight = 297;
      const pageEls = rootRef.current.querySelectorAll(".print-page");
      for (let i = 0; i < pageEls.length; i++) {
        const el = pageEls[i];
        // Collect link positions BEFORE rasterizing so we can overlay
        // clickable annotations on the rendered PDF page. Coords are stored
        // as fractions of the element's box, then mapped to the centered
        // image rect on the PDF page below.
        const elRect = el.getBoundingClientRect();
        const linkData = Array.from(el.querySelectorAll("a[href]"))
          .map((a) => {
            const r = a.getBoundingClientRect();
            if (!r.width || !r.height) return null;
            return {
              href: a.getAttribute("href"),
              fx: (r.left - elRect.left) / elRect.width,
              fy: (r.top - elRect.top) / elRect.height,
              fw: r.width / elRect.width,
              fh: r.height / elRect.height,
            };
          })
          .filter(Boolean);
        const canvas = await html2canvas(el, { backgroundColor: "#ffffff", scale: 2, useCORS: true });
        const img = canvas.toDataURL("image/png");
        const ratio = canvas.width / canvas.height;
        let w = pageWidth;
        let h = w / ratio;
        if (h > pageHeight) {
          h = pageHeight;
          w = h * ratio;
        }
        const x = (pageWidth - w) / 2;
        const y = (pageHeight - h) / 2;
        if (i > 0) pdf.addPage();
        pdf.addImage(img, "PNG", x, y, w, h, undefined, "FAST");
        for (const ld of linkData) {
          if (!ld.href) continue;
          const href = ld.href.startsWith("http") ? ld.href : ld.href.startsWith("www.") ? `https://${ld.href}` : ld.href;
          pdf.link(x + ld.fx * w, y + ld.fy * h, ld.fw * w, ld.fh * h, { url: href });
        }
      }
      const slug = (program.title || "program").replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "");
      pdf.save(`${slug}.pdf`);
      toast.success("Program PDF exported");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading || !program) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center text-[var(--tir-muted)]">
        Loading program…
      </div>
    );
  }

  return (
    <div data-testid="program-full-report-page" className="min-h-screen pb-20">
      <div className="no-print sticky top-0 z-40 bg-[var(--tir-ivory)]/95 backdrop-blur border-b border-[var(--tir-border)]">
        <div className="max-w-[900px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link
            to={`/coaching/programs/${id}/edit`}
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors"
          >
            <ArrowLeft size={14} /> Back to editor
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.print()}
              data-testid="print-full-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors"
            >
              <Printer size={14} /> Print
            </button>
            <button
              onClick={exportPDF}
              disabled={exporting}
              data-testid="download-full-btn"
              className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-50"
            >
              <FileDown size={14} /> {exporting ? "Exporting…" : "Download full PDF"}
            </button>
          </div>
        </div>
      </div>

      <div className="pt-8 px-4" ref={rootRef}>
        {pages.map((p) => (
          <React.Fragment key={p.key}>{p.node}</React.Fragment>
        ))}
      </div>
    </div>
  );
}
